import { useCallback, useEffect, useState } from "react";
import { listDocExports, type DocExport } from "@/lib/document/docExportClient";

type Props = {
  documentId: string;
  versionId?: string | null;
  refreshKey?: number;
};

function formatExportWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Recent registered PDF exports from doc_exports. */
export function DocumentExportHistorySection({ documentId, versionId, refreshKey = 0 }: Props) {
  const [exports, setExports] = useState<DocExport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listDocExports({
      documentId,
      versionId: versionId ?? undefined,
      limit: 15,
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setExports([]);
      return;
    }
    setExports(r.data.exports);
  }, [documentId, versionId]);

  useEffect(() => {
    void reload();
  }, [reload, refreshKey]);

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">Loading export history…</p>;
  }

  if (error) {
    return <p className="text-sm text-red-700 dark:text-red-300">{error}</p>;
  }

  if (exports.length === 0) {
    return (
      <p className="text-sm text-gray-600 dark:text-zinc-300">
        No registered exports yet. Exports appear here after Fill &amp; export completes storage
        registration.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700">
      {exports.map((row) => {
        const filledCount = Object.keys(row.variable_values ?? {}).length;
        return (
          <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
            <div>
              <p className="font-medium text-gray-900 dark:text-zinc-100">
                {formatExportWhen(row.exported_at)}
              </p>
              <p className="text-xs text-gray-500 dark:text-zinc-400">
                {formatByteSize(row.byte_size)}
                {filledCount > 0 ? ` · ${filledCount} filled variable${filledCount === 1 ? "" : "s"}` : ""}
              </p>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-wide text-gray-400 dark:text-zinc-500">
              {row.id.slice(0, 8)}…
            </span>
          </li>
        );
      })}
    </ul>
  );
}
